import React from 'react';
import { Building2, ChevronRight, LogOut, ShieldAlert } from 'lucide-react';
import type { EligibleTeam, OrgTeamRole } from '../../lib/teamSelection';

/**
 * TeamSelectionScreen — multi-team selection (Slice 2, plan §(c)).
 *
 * Shown AFTER role selection (RoleSelectionScreen is untouched and
 * always precedes this) when a user belongs to more than one eligible
 * organization team of the chosen role. It contains NO role-selection
 * UI — one card per team of the already-chosen role, nothing else.
 * Layout mirrors RoleSelectionScreen's card pattern and logout
 * affordance, rendered in the portal's LIGHT theme (owner directive
 * 2026-10-25: no dark themed page, ever): bright shell, white card,
 * dark text, clearly readable states.
 */

export const TEAM_ROLE_LABEL: Record<OrgTeamRole, string> = {
  referrer: 'Referrer',
  employer: 'Employer',
  sponsor: 'Sponsor',
};

interface TeamSelectionScreenProps {
  role: OrgTeamRole;
  teams: EligibleTeam[];
  /** Plain-language context shown above the list (e.g. after a revoked membership). */
  note?: string | null;
  onSelectTeam: (teamId: string) => void;
  onLogout: () => void;
}

export default function TeamSelectionScreen({
  role,
  teams,
  note,
  onSelectTeam,
  onLogout,
}: TeamSelectionScreenProps) {
  const label = TEAM_ROLE_LABEL[role];
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center bg-slate-100 p-4 sm:p-6 font-sans text-slate-800">
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xl space-y-6 animate-fade-in">
        <div className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 rounded-2xl btn-primary flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
            <Building2 className="h-6 w-6" />
          </div>
          <h2 className="text-xl sm:text-2xl font-black font-display text-slate-900 tracking-tight">
            Select Your {label} Team
          </h2>
          <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
            You belong to more than one {label} team. Choose the one you want to
            work in — you can switch at any time from your dashboard.
          </p>
        </div>

        {note && (
          <div
            role="status"
            className="flex items-start gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-700"
          >
            <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
            <span>{note}</span>
          </div>
        )}

        <div className="space-y-3">
          {teams.map((team) => {
            const restricted = role === 'referrer' && team.status.toLowerCase() === 'deactivated';
            return (
              <button
                key={team.id}
                onClick={() => onSelectTeam(team.id)}
                className="w-full text-left p-4 rounded-2xl bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-500 shadow-sm transition-all cursor-pointer group flex items-start gap-4"
              >
                <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors shrink-0 mt-0.5">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors break-words">
                      {team.name || `${label} Team`}
                    </h3>
                    <span
                      className={
                        team.role === 'super'
                          ? 'text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0'
                          : 'text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200 shrink-0'
                      }
                    >
                      {team.role === 'super' ? 'Super Agent' : 'Team Member'}
                    </span>
                  </div>
                  {restricted && (
                    <p className="mt-1.5">
                      <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
                        Restricted — read-only
                      </span>
                    </p>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all self-center shrink-0" />
              </button>
            );
          })}
        </div>

        <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-xs">
          <span className="text-slate-500">You can switch teams later from your dashboard.</span>
          <button
            onClick={onLogout}
            className="flex items-center gap-1.5 text-rose-600 hover:text-rose-500 font-bold transition cursor-pointer"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}
